import React, { useState, type ReactNode } from 'react';

/* ─── helpers ─── */
function cn(...args: Array<string | undefined | false | null>) {
    return args.filter(Boolean).join(' ');
}

/* ─── types ─── */
export type CardVariant = 'default' | 'elevated' | 'outlined' | 'glass' | 'gradient';
export type CardPadding = 'none' | 'compact' | 'normal' | 'spacious';

export interface CardProps {
    children: ReactNode;
    /** Card visual variant */
    variant?: CardVariant;
    /** Title rendered in header */
    title?: string;
    /** Description / subtitle rendered below title */
    description?: string;
    /** Action element rendered at right side of header */
    headerAction?: ReactNode;
    /** Footer content */
    footer?: ReactNode;
    /** Remove body padding */
    noPadding?: boolean;
    /** Padding preset */
    padding?: CardPadding;
    /** Enable hover lift effect */
    hoverable?: boolean;
    /** Top accent bar color */
    accentColor?: string;
    /** Custom border color */
    borderColor?: string;
    /** Loading overlay */
    loading?: boolean;
    className?: string;
    style?: React.CSSProperties;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
}

/* ─── padding map ─── */
const paddingClasses: Record<CardPadding, string> = {
    none: 'p-0',
    compact: 'p-[var(--v2-space-4)]',
    normal: 'p-[var(--v2-space-6)]',
    spacious: 'p-[var(--v2-space-8,2rem)]',
};

/* ─── component ─── */
const Card: React.FC<CardProps> = ({
    children,
    variant = 'default',
    title,
    description,
    headerAction,
    footer,
    noPadding = false,
    padding,
    hoverable = true,
    accentColor,
    borderColor,
    loading = false,
    className = '',
    style,
    onClick,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    /* Variant-specific overrides */
    const variantStyles: Record<CardVariant, React.CSSProperties> = {
        default: {},
        elevated: {
            boxShadow: isHovered
                ? 'var(--v2-shadow-card-hover)'
                : '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
        },
        outlined: {
            boxShadow: 'none',
            border: `1px solid ${borderColor || 'var(--v2-border-glass-strong)'}`,
        },
        glass: {
            background: 'var(--v2-glass-card)',
            backdropFilter: 'blur(16px) saturate(1.1)',
            WebkitBackdropFilter: 'blur(16px) saturate(1.1)',
        },
        gradient: {
            background: 'linear-gradient(135deg, var(--v2-bg-card), var(--v2-bg-surface))',
        },
    };

    /* Base card style — matches V2Card exactly */
    const cardStyle: React.CSSProperties = {
        background: 'var(--v2-bg-card)',
        border: `1px solid ${borderColor || 'var(--v2-border-card)'}`,
        borderRadius: 'var(--v2-radius-lg)',
        boxShadow: isHovered && hoverable
            ? 'var(--v2-shadow-card-hover)'
            : 'var(--v2-shadow-card)',
        transition: `all var(--v2-duration-normal) var(--v2-ease-calm)`,
        overflow: className.includes('overflow-visible') ? undefined : 'hidden',
        position: 'relative',
        transform: isHovered && hoverable ? 'translateY(-1px)' : 'translateY(0)',
        ...variantStyles[variant],
        ...style,
    };

    if (isHovered && hoverable && variant === 'default') {
        cardStyle.borderColor = borderColor || 'var(--v2-border-glass)';
    }

    /* Padding for body */
    const bodyPadding = noPadding
        ? ''
        : padding
            ? paddingClasses[padding]
            : 'p-[var(--v2-space-6)]';

    return (
        <div
            className={cn('relative', className)}
            style={cardStyle}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Accent bar */}
            {accentColor && (
                <div
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background: accentColor }}
                />
            )}

            {/* Loading overlay */}
            {loading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-[var(--v2-bg-card)]/80 backdrop-blur-sm">
                    <div
                        className="w-6 h-6 border-2 border-[var(--v2-primary-500)] border-t-transparent rounded-full"
                        style={{ animation: 'v2-btn-spin 0.6s linear infinite' }}
                    />
                </div>
            )}

            {/* Header */}
            {(title || headerAction) && (
                <div
                    className="flex items-center justify-between border-b border-[var(--v2-border-subtle)]"
                    style={{ padding: 'var(--v2-space-5) var(--v2-space-6)' }}
                >
                    <div className="min-w-0 flex-1">
                        {title && (
                            <h3
                                className="font-semibold tracking-[-0.015em] m-0"
                                style={{
                                    fontSize: 'var(--v2-font-card-title)',
                                    color: 'var(--v2-text-heading)',
                                }}
                            >
                                {title}
                            </h3>
                        )}
                        {description && (
                            <p
                                className="mt-1 m-0"
                                style={{
                                    fontSize: 'var(--v2-font-secondary)',
                                    color: 'var(--v2-text-secondary)',
                                    lineHeight: 'var(--v2-leading-relaxed)',
                                }}
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    {headerAction && (
                        <div className="ml-4 flex-shrink-0">{headerAction}</div>
                    )}
                </div>
            )}

            {/* Body */}
            <div className={bodyPadding}>
                {children}
            </div>

            {/* Footer */}
            {footer && (
                <div
                    className="border-t border-[var(--v2-border-subtle)]"
                    style={{
                        padding: 'var(--v2-space-4) var(--v2-space-6)',
                        background: 'rgba(0,0,0,0.02)',
                    }}
                >
                    {footer}
                </div>
            )}
        </div>
    );
};

export default Card;
